import { n as isContextError, t as createContext } from "./create-context-BhqTipOB.js";
import { basename, extname, join, resolve } from "node:path";
import * as clack from "@clack/prompts";
import { updateSettings } from "@clack/prompts";
import { P, attempt, attemptAsync, err, isNil, isPlainObject, isString, match, ok } from "@kidd-cli/utils/fp";
import yargs from "yargs";
import { hideBin } from "yargs/helpers";
import { z } from "zod";
import { writeFileSync } from "node:fs";
import { tmpdir } from "node:os";
import { match as match$1 } from "ts-pattern";
import { hasTag, withTag } from "@kidd-cli/utils/tag";
import { readdir } from "node:fs/promises";
import { path } from "@kidd-cli/utils/node";
import { validate } from "@kidd-cli/utils/validate";
//#region \0rolldown/runtime.js
var __defProp = Object.defineProperty;
var __exportAll = (all, no_symbols) => {
	let target = {};
	for (var name in all) __defProp(target, name, {
		get: all[name],
		enumerable: true
	});
	if (!no_symbols) __defProp(target, Symbol.toStringTag, { value: "Module" });
	return target;
};
//#endregion
//#region src/lib/crash.ts
/**
* Register global process handlers for uncaught exceptions and unhandled rejections.
*
* Converts raw runtime crashes into clean fatal error messages with debug logs.
* Without these handlers, compiled Bun binaries show raw stack traces with
* internal bundle line numbers that are meaningless to users.
*
* @param _name - The CLI name (reserved for future per-CLI log directories).
*/
function registerCrashHandlers(_name) {
	process.on("uncaughtException", handleUnexpectedError);
	process.on("unhandledRejection", handleUnexpectedError);
}
/**
* Handle a CLI error by logging the message and exiting with the appropriate code.
*
* ContextErrors are intentional user-facing errors (from `ctx.fail()`) and are
* displayed cleanly without a crash log. All other errors are delegated to
* {@link handleUnexpectedError} which writes a debug log to the OS temp directory.
*
* @param error - The caught error value.
*/
function exitOnError(error) {
	if (isContextError(error)) {
		clack.log.error(error.message);
		process.exit(error.exitCode);
		return;
	}
	handleUnexpectedError(error);
}
/**
* Handle an unexpected (non-ContextError) error by writing a crash log and exiting.
*
* Resolves the error to an Error instance, writes a debug log to `/tmp`,
* prints a clean message, and exits with the default exit code.
*
* @private
* @param error - The caught error value.
*/
function handleUnexpectedError(error) {
	const resolved = match(error).with(P.instanceOf(Error), (e) => e).otherwise((e) => new Error(String(e)));
	const logPath = writeCrashLog(resolved);
	clack.log.error(resolved.message);
	if (logPath) clack.log.message(`Debug log: ${logPath}`);
	process.exit(1);
}
/**
* Format the error cause as a log line, or return an empty array when absent.
*
* @private
* @param cause - The error's `cause` value, if any.
* @returns An array with zero or one formatted cause line.
*/
function formatCause(cause) {
	if (cause === void 0) return [];
	return [`Cause: ${String(cause)}`];
}
/**
* Write a crash log to the OS temp directory.
*
* Includes the error message, stack trace, and basic environment info.
* Silently returns `undefined` if the write fails — crash logging must
* never itself cause a crash.
*
* @private
* @param error - The error to log.
* @returns The path to the written log file, or `undefined` on failure.
*/
function writeCrashLog(error) {
	const now = (/* @__PURE__ */ new Date()).toISOString();
	const filename = `kidd-crash-${now.replaceAll(":", "-")}.log`;
	const logPath = join(tmpdir(), filename);
	const lines = [
		"Kidd Crash Log",
		`Timestamp: ${now}`,
		`Node: ${process.version}`,
		`Platform: ${process.platform} ${process.arch}`,
		`CWD: ${process.cwd()}`,
		"",
		`Error: ${error.message}`,
		"",
		error.stack ?? "(no stack trace)",
		"",
		...formatCause(error.cause)
	];
	const [writeError] = attempt(() => writeFileSync(logPath, lines.join("\n"), "utf8"));
	if (writeError) return;
	return logPath;
}
//#endregion
//#region src/lib/debug.ts
/**
* Check whether debug mode is enabled via the `KIDD_DEBUG` or `DEBUG`
* environment variables.
*
* `KIDD_DEBUG` takes precedence; `DEBUG` is honored when `KIDD_DEBUG` is unset.
*
* Truthy values: `"true"`, `"1"`
* Falsy values: `"false"`, `"0"`, `undefined`, `null`
*
* @returns `true` when `KIDD_DEBUG` or `DEBUG` is set to a truthy value.
*/
function isDebug() {
	const value = process.env.KIDD_DEBUG ?? process.env.DEBUG;
	return value === "true" || value === "1";
}
//#endregion
//#region src/autoload.ts
const VALID_EXTENSIONS = new Set([
	".ts",
	".js",
	".mjs",
	".tsx",
	".jsx"
]);
const INDEX_NAME = "index";
/**
* Scan a directory for command files and produce a CommandMap.
*
* @param options - Autoload configuration (directory override, etc.).
* @returns A promise resolving to a CommandMap built from the directory tree.
*/
async function autoload(options) {
	const dir = resolveDir(options);
	return resolveCommandMapFromEntries(dir, await readdir(dir, { withFileTypes: true }));
}
/**
* Resolve the target directory from autoload options.
*
* @private
* @param options - Optional autoload configuration.
* @returns The resolved absolute directory path.
*/
function resolveDir(options) {
	if (options && isString(options.dir)) return resolve(options.dir);
	return resolve("./commands");
}
/**
* Scan a subdirectory and assemble it as a parent command with subcommands.
*
* If the directory contains an `index.ts`/`index.js`, that becomes the parent
* handler. Otherwise a handler-less group command is created that demands a
* subcommand.
*
* @private
* @param dir - Absolute path to the subdirectory.
* @returns A tuple of [name, Command] or undefined if the directory is empty.
*/
async function resolveDirCommand(dir) {
	const dirName = basename(dir);
	const dirEntries = await readdir(dir, { withFileTypes: true });
	const subCommands = await resolveCommandMapFromEntries(dir, dirEntries);
	const indexFile = findIndexInEntries(dirEntries);
	if (indexFile) {
		const parentCommand = await importCommand(join(dir, indexFile.name));
		if (parentCommand) return [parentCommand.name ?? dirName, withTag({
			...parentCommand,
			commands: subCommands
		}, "Command")];
	}
	if (Object.keys(subCommands).length === 0) return;
	return [dirName, withTag({ commands: subCommands }, "Command")];
}
/**
* Build a CommandMap from pre-read directory entries.
*
* Shared by both `autoload` and `resolveDirCommand` to avoid duplicating
* the file/dir fan-out and result-filtering logic.
*
* @private
* @param dir - Absolute path to the directory the entries belong to.
* @param entries - Pre-read directory entries for that directory.
* @returns A CommandMap built from the entries.
*/
async function resolveCommandMapFromEntries(dir, entries) {
	const fileEntries = entries.filter(isCommandFile);
	const dirEntries = entries.filter(isCommandDir);
	const fileResults = await Promise.all(fileEntries.map(async (entry) => {
		const cmd = await importCommand(join(dir, entry.name));
		if (!cmd) return;
		return [cmd.name ?? deriveCommandName(entry), cmd];
	}));
	const dirResults = await Promise.all(dirEntries.map((entry) => resolveDirCommand(join(dir, entry.name))));
	const validPairs = [...fileResults, ...dirResults].filter((pair) => pair !== void 0);
	return Object.fromEntries(deduplicateCommandPairs(validPairs));
}
/**
* Find the index file (index.ts or index.js) in pre-read directory entries.
*
* @private
* @param entries - Pre-read directory entries.
* @returns The index file's Dirent or undefined.
*/
function findIndexInEntries(entries) {
	return entries.find((entry) => entry.isFile() && !entry.name.endsWith(".d.ts") && !entry.name.endsWith(".d.tsx") && VALID_EXTENSIONS.has(extname(entry.name)) && basename(entry.name, extname(entry.name)) === INDEX_NAME);
}
/**
* Dynamically import a file and validate that its default export is a Command.
*
* Converts the absolute filesystem path to a `file://` URL before passing to
* `import()` so resolution works on Windows (where backslash paths are not
* valid ESM specifiers).
*
* @private
* @param filePath - Absolute path to the file to import.
* @returns The Command if valid, or undefined.
*/
async function importCommand(filePath) {
	const specifier = path.toImportUrl(filePath);
	try {
		const mod = await import(specifier);
		if (isCommandExport(mod)) return mod.default;
		return;
	} catch (error) {
		if (isDebug()) console.warn(`[kidd] failed to import command from ${specifier}:`, error);
		return;
	}
}
/**
* Check whether a module's default export is a Command object.
*
* ES module namespace objects have a null prototype, so isPlainObject
* rejects them. We only need to verify the namespace is a non-null
* object with a default export that is a plain Command object.
*
* @private
* @param mod - The imported module to inspect.
* @returns True when the module has a Command as its default export.
*/
function isCommandExport(mod) {
	return match$1(mod).when((value) => typeof value === "object" && value !== null && Object.hasOwn(value, "default"), (value) => {
		const def = value.default;
		return isPlainObject(def) && hasTag(def, "Command");
	}).otherwise(() => false);
}
/**
* Derive a command name from a directory entry by stripping its extension.
*
* @private
* @param entry - The directory entry to derive the name from.
* @returns The file name without its extension.
*/
function deriveCommandName(entry) {
	return basename(entry.name, extname(entry.name));
}
/**
* Predicate: entry is a command file (.ts/.js, not index, not _/. prefixed).
*
* @private
* @param entry - The directory entry to check.
* @returns True when the entry is a valid command file.
*/
function isCommandFile(entry) {
	if (!entry.isFile()) return false;
	if (entry.name.startsWith("_") || entry.name.startsWith(".")) return false;
	if (entry.name.endsWith(".d.ts") || entry.name.endsWith(".d.tsx")) return false;
	if (!VALID_EXTENSIONS.has(extname(entry.name))) return false;
	return deriveCommandName(entry) !== INDEX_NAME;
}
/**
* Predicate: entry is a scannable command directory (not _/. prefixed).
*
* @private
* @param entry - The directory entry to check.
* @returns True when the entry is a valid command directory.
*/
function isCommandDir(entry) {
	if (!entry.isDirectory()) return false;
	return !entry.name.startsWith("_") && !entry.name.startsWith(".");
}
/**
* Deduplicate command pairs by name, keeping the first occurrence.
*
* When multiple commands resolve to the same name (e.g. via explicit `name`
* overrides), this ensures a deterministic first-wins policy and emits a
* warning for every collision so the user can fix the conflict.
*
* @private
* @param pairs - The resolved [name, Command] tuples.
* @returns Deduplicated pairs with only the first occurrence of each name.
*/
function deduplicateCommandPairs(pairs) {
	const { result } = pairs.reduce((acc, pair) => {
		const [name] = pair;
		if (acc.seen.has(name)) {
			console.warn(`[kidd] duplicate command name "${name}" — first definition wins, later definition ignored`);
			return acc;
		}
		return {
			result: [...acc.result, pair],
			seen: new Set([...acc.seen, name])
		};
	}, {
		result: [],
		seen: /* @__PURE__ */ new Set()
	});
	return result;
}
//#endregion
//#region src/command.ts
/**
* Check whether a value is a structured {@link CommandsConfig} object.
*
* Discriminates from a plain `CommandMap` by checking for the `path` (string)
* key — which cannot appear on a valid `CommandMap` whose values are tagged
* `Command` objects.
*
* @param value - The value to test.
* @returns `true` when `value` is a `CommandsConfig`.
*/
function isCommandsConfig(value) {
	if (typeof value !== "object" || value === null || value instanceof Promise) return false;
	return Object.hasOwn(value, "path") && typeof value.path === "string";
}
/**
* Define a CLI command with typed options, positionals, config, and handler.
*
* The `const TMiddleware` generic preserves the middleware tuple as a literal type,
* enabling TypeScript to extract and intersect `Variables` from each middleware
* element onto the handler's `ctx` type.
*
* When `def.commands` is a structured {@link CommandsConfig}, the factory
* normalizes it into a flat `commands` field on the output `Command` object
* so downstream consumers never need to handle the grouped form.
*
* @param def - Command definition including description, options, positionals, middleware, and handler.
* @returns A resolved Command object for registration in the command map.
*/
function command(def) {
	const resolved = {
		...def,
		deprecated: resolveValue$1(def.deprecated),
		description: resolveValue$1(def.description),
		hidden: resolveValue$1(def.hidden)
	};
	return match(resolved.commands).when(isCommandsConfig, (cfg) => {
		const { commands: innerCommands } = cfg;
		return withTag({
			...resolved,
			commands: innerCommands
		}, "Command");
	}).otherwise(() => withTag({ ...resolved }, "Command"));
}
/**
* Resolve a {@link Resolvable} value by invoking it if it is a function,
* or returning the value directly.
*
* @private
* @param value - A static value or zero-argument factory function.
* @returns The resolved value, or `undefined` when the input is `undefined`.
*/
function resolveValue$1(value) {
	if (typeof value === "function") return value();
	return value;
}
//#endregion
//#region src/runtime/args/zod.ts
/**
* Type guard that checks whether a value is a zod object schema.
*
* @param args - The value to check.
* @returns True when args is a ZodObject.
*/
function isZodSchema(args) {
	return typeof args === "object" && args !== null && Object.hasOwn(args, "_def") && typeof args._def === "object" && args._def !== null && args._def.type === "object";
}
/**
* Convert an entire zod object schema into a record of yargs options.
*
* @param schema - The zod object schema.
* @returns A record mapping field names to yargs option definitions.
*/
function zodSchemaToYargsOptions(schema) {
	return mapSchemaShape(schema, (fieldSchema) => buildZodFieldOption(fieldSchema, resolveZodYargsType));
}
/**
* Extract positional metadata from a zod object schema.
*
* Returns an ordered array of positional names with their required/optional status.
* Key order from `Object.keys(schema.shape)` determines positional order.
*
* @param schema - The zod object schema for positionals.
* @returns An ordered array of positional metadata.
*/
function zodSchemaToPositionalMeta(schema) {
	return getShapeEntries(schema).map(([key, fieldSchema]) => {
		const { isOptional } = unwrapZodType(fieldSchema);
		return {
			isOptional,
			name: key
		};
	});
}
/**
* Convert a zod object schema into a record of yargs positional options.
*
* Each field in the schema becomes a positional argument. Positionals support
* `string`, `number`, and `boolean` types — other types fall back to `string`.
*
* @param schema - The zod object schema for positionals.
* @returns A record mapping field names to yargs positional option definitions.
*/
function zodSchemaToYargsPositionals(schema) {
	return mapSchemaShape(schema, (fieldSchema) => buildZodFieldOption(fieldSchema, resolvePositionalType));
}
/**
* Extract typed shape entries from a zod object schema.
*
* @private
* @param schema - The zod object schema.
* @returns An array of [key, fieldSchema] pairs.
*/
function getShapeEntries(schema) {
	const shape = schema.shape;
	return Object.entries(shape);
}
/**
* Map every field in a zod object schema through a converter, returning a record.
*
* @private
* @param schema - The zod object schema.
* @param converter - A function converting a single zod field schema to a yargs option.
* @returns A record mapping field names to converted option definitions.
*/
function mapSchemaShape(schema, converter) {
	return Object.fromEntries(getShapeEntries(schema).map(([key, fieldSchema]) => [key, converter(fieldSchema)]));
}
/**
* Extract a default value from a zod definition, falling back to the provided value.
*
* @private
* @param def - The zod definition to inspect.
* @param fallback - Value to return when no default is defined.
* @returns The resolved default value.
*/
function resolveDefaultValue(def, fallback) {
	if (def.defaultValue !== void 0) return def.defaultValue;
	return fallback;
}
/**
* Unwrap a ZodOptional type, recursing into the inner type.
*
* @private
* @param options - The unwrap options containing def, current type, and default value.
* @returns Unwrapped type information.
*/
function unwrapOptional(options) {
	const { def, current, defaultValue } = options;
	if (def.innerType) return unwrapZodTypeRecursive({
		current: def.innerType,
		defaultValue,
		isOptional: true
	});
	return {
		defaultValue,
		inner: current,
		isOptional: true
	};
}
/**
* Unwrap a ZodDefault type, resolving its default value and recursing.
*
* @private
* @param options - The unwrap options containing def, current type, and default value.
* @returns Unwrapped type information with the resolved default.
*/
function unwrapDefault(options) {
	const { def, current, defaultValue } = options;
	const newDefault = resolveDefaultValue(def, defaultValue);
	if (def.innerType) return unwrapZodTypeRecursive({
		current: def.innerType,
		defaultValue: newDefault,
		isOptional: true
	});
	return {
		defaultValue: newDefault,
		inner: current,
		isOptional: true
	};
}
/**
* Recursively unwrap optional and default wrappers from a zod type.
*
* @private
* @param options - The recursive unwrap options containing current type, optionality flag, and default value.
* @returns The fully unwrapped type information.
*/
function unwrapZodTypeRecursive(options) {
	const { current, isOptional, defaultValue } = options;
	const def = current._def;
	if (def.type === "optional") return unwrapOptional({
		current,
		def,
		defaultValue
	});
	if (def.type === "default") return unwrapDefault({
		current,
		def,
		defaultValue
	});
	return {
		defaultValue,
		inner: current,
		isOptional
	};
}
/**
* Unwrap a zod schema to extract its base type, optionality, and default value.
*
* @private
* @param schema - The zod type to unwrap.
* @returns The unwrapped type information.
*/
function unwrapZodType(schema) {
	return unwrapZodTypeRecursive({
		current: schema,
		defaultValue: void 0,
		isOptional: false
	});
}
/**
* Map a zod type name to a yargs option type string.
*
* @private
* @param typeName - The zod type name (e.g. 'string', 'number').
* @returns The corresponding yargs type.
*/
function resolveZodYargsType(typeName) {
	return match$1(typeName).with("string", () => "string").with("number", () => "number").with("boolean", () => "boolean").with("array", () => "array").otherwise(() => "string");
}
/**
* Map a type name to a valid yargs positional type.
*
* Positionals support `'string'`, `'number'`, and `'boolean'`.
* All other types fall back to `'string'`. Used for both Zod and
* yargs-native positional definitions.
*
* @param typeName - The type name to resolve.
* @returns A positional-compatible type.
*/
function resolvePositionalType(typeName) {
	return match$1(typeName).with("number", () => "number").with("boolean", () => "boolean").otherwise(() => "string");
}
/**
* Extract enum choices from a zod definition when it represents a ZodEnum.
*
* @private
* @param def - The zod definition to inspect.
* @returns An object with `choices` if the def is an enum, otherwise empty.
*/
function resolveEnumChoices(def) {
	if (def.type === "enum" && def.entries) return { choices: Object.values(def.entries) };
	return {};
}
/**
* Build a base yargs option from a zod schema's description and default.
*
* @private
* @param inner - The unwrapped zod schema instance.
* @param defaultValue - The resolved default value.
* @returns A partial yargs option object.
*/
function buildBaseOption(inner, defaultValue) {
	const { description } = inner;
	const base = {};
	if (description) base.describe = description;
	if (defaultValue !== void 0) base.default = defaultValue;
	return base;
}
/**
* Convert a single zod field schema into a yargs option or positional definition.
*
* Accepts a type resolver to support both full options (string/number/boolean/array)
* and positionals (string/number only).
*
* @private
* @param schema - A single zod field type.
* @param typeResolver - Function to map zod type name to a yargs-compatible type.
* @returns A yargs option/positional object.
*/
function buildZodFieldOption(schema, typeResolver) {
	const { inner, isOptional, defaultValue } = unwrapZodType(schema);
	const innerDef = inner._def;
	const base = {
		...buildBaseOption(inner, defaultValue),
		...resolveEnumChoices(innerDef),
		type: typeResolver(innerDef.type)
	};
	if (!isOptional) return {
		...base,
		demandOption: true
	};
	return base;
}
//#endregion
//#region src/runtime/args/parser.ts
/**
* Create an args parser that cleans and validates raw parsed arguments.
*
* Captures both option and positional definitions in a closure and returns an
* ArgsParser whose `parse` method strips yargs-internal keys and validates
* against a merged zod schema when one is defined.
*
* @param defs - The option and positional definitions from the command.
* @returns An ArgsParser with a parse method.
*/
function createArgsParser(defs) {
	const mergedSchema = buildMergedSchema(defs.options, defs.positionals);
	return { parse(rawArgs) {
		return validateArgs(mergedSchema, cleanParsedArgs(rawArgs));
	} };
}
/**
* Build a merged Zod schema from options and positionals definitions.
*
* When both are Zod schemas, merges them into a single schema for validation.
* When only one is a Zod schema, returns that schema with `.passthrough()` so
* yargs-native keys from the other side are preserved. When neither is Zod,
* returns undefined (no validation).
*
* @private
* @param options - Option definitions (flags).
* @param positionals - Positional argument definitions.
* @returns A merged Zod schema or undefined.
*/
function buildMergedSchema(options, positionals) {
	const optionsIsZod = options && isZodSchema(options);
	const positionalsIsZod = positionals && isZodSchema(positionals);
	if (optionsIsZod && positionalsIsZod) return options.merge(positionals);
	if (optionsIsZod) return options.passthrough();
	if (positionalsIsZod) return positionals.passthrough();
}
/**
* Strip yargs-internal keys (`_`, `$0`) and camelCase-duplicated hyphenated keys
* from a parsed argv record, returning only user-defined arguments.
*
* @private
* @param argv - Raw parsed argv from yargs.
* @returns A cleaned record containing only user-defined arguments.
*/
function cleanParsedArgs(argv) {
	return Object.fromEntries(Object.entries(argv).filter(([key]) => key !== "_" && key !== "$0" && !key.includes("-")));
}
/**
* Validate parsed arguments against a zod schema when one is defined.
*
* When no zod schema is present, the parsed args are returned as-is.
* When a zod schema is present, validation is performed and a Result error
* is returned on failure.
*
* @private
* @param schema - The merged zod schema or undefined.
* @param parsedArgs - The cleaned parsed arguments.
* @returns A Result containing validated arguments (zod-parsed when applicable).
*/
function validateArgs(schema, parsedArgs) {
	if (!schema) return ok(parsedArgs);
	return validate({
		schema,
		params: parsedArgs,
		createError: ({ message }) => /* @__PURE__ */ new Error(`Invalid arguments:\n  ${message}`)
	});
}
//#endregion
//#region src/runtime/args/register.ts
/**
* Register option and positional definitions on a yargs builder.
*
* Accepts `options` (flags) and `positionals` as either zod object schemas or
* records of yargs-native arg definitions. Options are registered via
* `builder.option()` and positionals via `builder.positional()`.
*
* @param params - Builder instance, option definitions, and positional definitions.
* @returns Nothing; mutates the yargs builder in place.
*/
function registerCommandArgs({ builder, options, positionals }) {
	if (positionals) registerArgsDef({
		builder,
		def: positionals,
		register: (b, key, opt) => b.positional(key, opt),
		toYargsNative: yargsArgDefToPositional,
		toZod: zodSchemaToYargsPositionals
	});
	if (options) registerArgsDef({
		builder,
		def: options,
		register: (b, key, opt) => b.option(key, opt),
		toYargsNative: yargsArgDefToOption,
		toZod: zodSchemaToYargsOptions
	});
}
/**
* Register an `ArgsDef` (Zod or yargs-native) on a yargs builder.
*
* @private
* @param params - Registration parameters including the builder, definition, and converters.
*/
function registerArgsDef(params) {
	const { builder, def, register, toZod, toYargsNative } = params;
	if (isZodSchema(def)) {
		const opts = toZod(def);
		Object.entries(opts).map(([key, opt]) => register(builder, key, opt));
	} else Object.entries(def).map(([key, argDef]) => register(builder, key, toYargsNative(argDef)));
}
/**
* Convert a yargs-native arg definition into a yargs option object.
*
* @private
* @param def - The yargs arg definition.
* @returns A yargs option object.
*/
function yargsArgDefToOption(def) {
	return {
		alias: def.alias,
		choices: def.choices,
		default: def.default,
		demandOption: def.required ?? false,
		deprecated: resolveValue(def.deprecated),
		describe: def.description,
		group: def.group,
		hidden: resolveValue(def.hidden) ?? false,
		type: def.type
	};
}
/**
* Convert a yargs-native arg definition into a yargs positional option object.
*
* @private
* @param def - The yargs arg definition for a positional.
* @returns A yargs positional option object.
*/
function yargsArgDefToPositional(def) {
	return {
		choices: def.choices,
		default: def.default,
		demandOption: def.required ?? false,
		describe: def.description,
		type: resolvePositionalType(def.type)
	};
}
/**
* Resolve a {@link Resolvable} value by invoking it if it is a function,
* or returning the value directly.
*
* @private
* @param value - A static value or zero-argument factory function.
* @returns The resolved value, or `undefined` when the input is `undefined`.
*/
function resolveValue(value) {
	if (typeof value === "function") return value();
	return value;
}
//#endregion
//#region src/runtime/sort-commands.ts
/**
* Validate that every name in the order array exists in the provided command names.
*
* @param params - The order array and available command names to validate against.
* @returns A Result tuple — `[null, void]` on success or `[Error, null]` on failure.
*/
function validateCommandOrder(params) {
	const { order, commandNames } = params;
	const seen = /* @__PURE__ */ new Set();
	const duplicates = order.filter((name) => {
		if (seen.has(name)) return true;
		seen.add(name);
		return false;
	});
	if (duplicates.length > 0) return err(`Invalid command order: duplicate command(s) ${[...new Set(duplicates)].map((n) => `"${n}"`).join(", ")}`);
	const nameSet = new Set(commandNames);
	const invalid = order.filter((name) => !nameSet.has(name));
	if (invalid.length > 0) return err(`Invalid command order: unknown command(s) ${invalid.map((n) => `"${n}"`).join(", ")}`);
	return ok();
}
/**
* Sort command entries with ordered names first (in specified order),
* remaining names alphabetically.
*
* @param params - The command entries and optional order array.
* @returns Sorted array of `[name, Command]` entries.
*/
function sortCommandEntries(params) {
	const { entries, order } = params;
	if (!order || order.length === 0) return [...entries].toSorted(([a], [b]) => a.localeCompare(b));
	const entryMap = new Map(entries);
	const ordered = order.filter((name) => entryMap.has(name)).map((name) => [name, entryMap.get(name)]);
	const orderedSet = new Set(order);
	const remaining = entries.filter(([name]) => !orderedSet.has(name)).toSorted(([a], [b]) => a.localeCompare(b));
	return [...ordered, ...remaining];
}
//#endregion
//#region src/runtime/register.ts
/**
* Type guard that checks whether a value is a Command object.
*
* @param value - The value to test.
* @returns True when the value has `[TAG] === 'Command'`.
*/
function isCommand(value) {
	return hasTag(value, "Command");
}
/**
* Register all commands from a CommandMap on a yargs instance.
*
* Iterates over the command map, filters for valid Command objects,
* validates the order array, sorts entries, and recursively registers
* each command (including subcommands) on the provided yargs Argv instance.
*
* @param options - Registration options including the command map, yargs instance, and resolution ref.
*/
function registerCommands(options) {
	const { instance, commands, resolved, parentPath, order, errorRef } = options;
	const commandEntries = Object.entries(commands).filter((pair) => isCommand(pair[1])).map(([key, entry]) => [entry.name ?? key, entry]);
	if (order && order.length > 0) {
		const [validationError] = validateCommandOrder({
			commandNames: commandEntries.map(([name]) => name),
			order
		});
		if (validationError && errorRef) {
			errorRef.error = validationError;
			return;
		}
	}
	sortCommandEntries({
		entries: commandEntries,
		order
	}).map(([name, entry]) => registerSingleCommand({
		builder: instance,
		cmd: entry,
		errorRef,
		instance,
		name,
		parentPath,
		resolved
	}));
}
/**
* Register a single resolved command (and its subcommands) with yargs.
*
* Sets up the yargs command handler, wires argument definitions, and
* recursively registers any nested subcommands. On match, stores the
* resolved handler and command path in the shared ref.
*
* @private
* @param options - Command registration context.
*/
function registerSingleCommand(options) {
	const { instance, name, cmd, resolved, parentPath, errorRef } = options;
	const commandSpec = formatCommandSpec(formatCommandString(name, cmd.positionals), cmd.aliases);
	const builder = (yargsBuilder) => {
		if (cmd.strict !== void 0) yargsBuilder.strict(cmd.strict);
		registerCommandArgs({
			builder: yargsBuilder,
			options: cmd.options,
			positionals: cmd.positionals
		});
		if (cmd.commands) {
			const subCommands = Object.entries(cmd.commands).filter((pair) => isCommand(pair[1])).map(([key, entry]) => [entry.name ?? key, entry]);
			const subOrder = cmd.help?.order;
			if (subOrder && subOrder.length > 0) {
				const [validationError] = validateCommandOrder({
					commandNames: subCommands.map(([n]) => n),
					order: subOrder
				});
				if (validationError && errorRef) {
					errorRef.error = validationError;
					return yargsBuilder;
				}
			}
			sortCommandEntries({
				entries: subCommands,
				order: subOrder
			}).map(([subName, subEntry]) => registerSingleCommand({
				builder: yargsBuilder,
				cmd: subEntry,
				errorRef,
				instance: yargsBuilder,
				name: subName,
				parentPath: [...parentPath, name],
				resolved
			}));
			if (cmd.handler || cmd.render) yargsBuilder.demandCommand(0);
			else yargsBuilder.demandCommand(1, "You must specify a subcommand.");
		}
		return yargsBuilder;
	};
	const handler = () => {
		resolved.ref = {
			commandPath: [...parentPath, name],
			handler: cmd.handler,
			middleware: cmd.middleware ?? [],
			options: cmd.options,
			positionals: cmd.positionals,
			render: cmd.render
		};
	};
	if (cmd.hidden === true) instance.command(commandSpec, false, builder, handler, [], cmd.deprecated);
	else instance.command(commandSpec, cmd.description ?? "", builder, handler, [], cmd.deprecated);
}
/**
* Build a yargs command string with positional placeholders.
*
* Normalizes both Zod and yargs-native positional definitions to a common
* intermediate representation, then formats each as `<name>` (required) or
* `[name]` (optional).
*
* @private
* @param name - The base command name.
* @param positionals - Optional positional definitions (Zod schema or yargs-native record).
* @returns The command string with positional placeholders appended.
*/
function formatCommandString(name, positionals) {
	if (!positionals) return name;
	const meta = extractPositionalMeta(positionals);
	return match$1(meta.length).with(0, () => name).otherwise(() => [name, ...meta.map(formatPlaceholder)].join(" "));
}
/**
* Normalize an `ArgsDef` into an ordered array of positional metadata.
*
* Handles both Zod schemas (via `zodSchemaToPositionalMeta`) and yargs-native
* records (via `Object.entries` with `required` check).
*
* @private
* @param positionals - The positional definitions.
* @returns An ordered array of positional metadata.
*/
function extractPositionalMeta(positionals) {
	if (isZodSchema(positionals)) return zodSchemaToPositionalMeta(positionals);
	return Object.entries(positionals).map(([key, def]) => ({
		isOptional: def.required !== true,
		name: key
	}));
}
/**
* Format a positional metadata entry as a yargs placeholder string.
*
* @private
* @param meta - The positional metadata.
* @returns `<name>` for required positionals, `[name]` for optional ones.
*/
function formatPlaceholder(meta) {
	if (meta.isOptional) return `[${meta.name}]`;
	return `<${meta.name}>`;
}
/**
* Build the first argument to `yargs.command()`.
*
* Returns a plain string when there are no aliases, or a `[commandString, ...aliases]`
* array when aliases are present — both forms are accepted by yargs.
*
* @private
* @param commandString - The primary command string (may include positional placeholders).
* @param aliases - Optional alternative names for the command.
* @returns A string or string array suitable for `yargs.command()`.
*/
function formatCommandSpec(commandString, aliases) {
	return match$1(aliases).with(void 0, () => commandString).otherwise((a) => match$1(a.length).with(0, () => commandString).otherwise(() => [commandString, ...a]));
}
//#endregion
//#region src/runtime/runner.ts
/**
* Create a runner that executes root and command middleware chains.
*
* Root middleware wraps the command middleware chain, which in turn wraps
* the command handler — producing a nested onion lifecycle:
*
* ```
* root middleware start →
*   command middleware start →
*     handler
*   command middleware end
* root middleware end
* ```
*
* @param rootMiddleware - Root-level middleware from `cli({ middleware })`.
* @returns A MiddlewareExecutor with an execute method.
*/
function createMiddlewareExecutor(rootMiddleware) {
	return { async execute({ ctx, handler, middleware }) {
		const commandHandler = async (innerCtx) => {
			await runMiddlewareChain(middleware, innerCtx, handler);
		};
		await runMiddlewareChain(rootMiddleware, ctx, commandHandler);
	} };
}
/**
* Execute a middleware chain followed by a final handler.
*
* Runs each middleware in order, passing `ctx` and a `next` callback.
* When all middleware have called `next()`, the final handler is invoked.
* A middleware can short-circuit by not calling `next()`.
*
* @private
* @param middlewares - Ordered array of middleware to execute.
* @param ctx - The context object threaded through middleware and handler.
* @param finalHandler - The command handler to invoke after all middleware.
*/
async function runMiddlewareChain(middlewares, ctx, finalHandler) {
	async function executeChain(index) {
		if (index >= middlewares.length) {
			await finalHandler(ctx);
			return;
		}
		const mw = middlewares[index];
		if (mw) await mw.handler(ctx, () => executeChain(index + 1));
	}
	await executeChain(0);
}
//#endregion
//#region src/runtime/runtime.ts
/**
* Create a runtime that orchestrates middleware execution.
*
* Captures middleware in a closure alongside a runner.
* The returned `runtime.execute` method handles arg parsing, context creation,
* and middleware chain execution for each command invocation.
*
* @param options - Runtime configuration including name, version, and middleware.
* @returns A ResultAsync containing the runtime or an error.
*/
async function createRuntime(options) {
	const runner = createMiddlewareExecutor(options.middleware ?? []);
	return ok({ async execute(command) {
		const [argsError, validatedArgs] = createArgsParser({
			options: command.options,
			positionals: command.positionals
		}).parse(command.rawArgs);
		if (argsError) return err(argsError);
		const ctx = createContext({
			args: validatedArgs,
			argv: options.argv,
			display: options.display,
			log: options.log,
			meta: {
				command: [...command.commandPath],
				dirs: options.dirs,
				name: options.name,
				version: options.version
			},
			prompts: options.prompts,
			status: options.status
		});
		const finalHandler = command.render ?? command.handler ?? (async () => {});
		const [execError] = await attemptAsync(() => runner.execute({
			ctx,
			handler: finalHandler,
			middleware: command.middleware
		}));
		if (execError) return err(execError);
		return ok();
	} });
}
//#endregion
//#region src/cli.ts
var cli_exports = /* @__PURE__ */ __exportAll({
	cli: () => cli,
	default: () => cli
});
/**
* Bootstrap and run the CLI application.
*
* Parses argv, resolves the matched command, loads config, runs the
* middleware chain, and invokes the command handler.
*
* @param options - CLI configuration including name, version, commands, and middleware.
*/
async function cli(options) {
	registerCrashHandlers(options.name);
	const [uncaughtError, result] = await attemptAsync(async () => {
		const [versionError, version] = resolveVersion(options.version);
		if (versionError) return versionError;
		const rawTokens = hideBin(process.argv);
		const program = yargs(rawTokens).scriptName(options.name).version(version).alias("version", "v").strict(options.strict !== false).help().alias("help", "h").option("cwd", {
			describe: "Set the working directory",
			global: true,
			type: "string"
		});
		if (options.description) program.usage(options.description);
		const footer = extractFooter(options.help);
		if (footer) program.epilogue(footer);
		const resolved = { ref: void 0 };
		const errorRef = { error: void 0 };
		const resolvedCmds = await resolveCommands(options.commands);
		if (resolvedCmds) {
			registerCommands({
				commands: resolvedCmds.commands,
				errorRef,
				instance: program,
				order: options.help?.order,
				parentPath: [],
				resolved
			});
			if (errorRef.error) return errorRef.error;
		}
		const argv = await program.parseAsync();
		applyCwd(argv);
		if (!resolved.ref) {
			showNoCommandHelp({
				argv,
				commands: resolvedCmds,
				help: options.help,
				program
			});
			return;
		}
		const dirs = resolveDirs(options.name, options.dirs);
		applyDisplayGlobals(options.display);
		const [runtimeError, runtime] = await createRuntime({
			argv: [String(argv.$0), ...rawTokens],
			dirs,
			display: options.display,
			log: options.log,
			middleware: options.middleware,
			name: options.name,
			prompts: options.prompts,
			status: options.status,
			version
		});
		if (runtimeError) return runtimeError;
		const [executeError] = await runtime.execute({
			commandPath: resolved.ref.commandPath,
			handler: resolved.ref.handler,
			middleware: resolved.ref.middleware,
			options: resolved.ref.options,
			positionals: resolved.ref.positionals,
			rawArgs: argv,
			render: resolved.ref.render
		});
		return executeError;
	});
	if (uncaughtError) {
		exitOnError(uncaughtError);
		return;
	}
	if (result) exitOnError(result);
}
const VERSION_ERROR = /* @__PURE__ */ new Error("No CLI version available. Either pass `version` to cli() or build with the kidd bundler.");
const VersionSchema = z.string().trim().min(1);
/**
* Resolve the CLI version from an explicit value or the compile-time constant.
*
* Resolution order:
* 1. Explicit version string passed to `cli()`
* 2. `__KIDD_VERSION__` injected by the kidd bundler at build time
*
* Returns an error when neither source provides a non-empty version.
*
* @private
* @param explicit - The version string from `CliOptions.version`, if provided.
* @returns A Result tuple with the resolved version string or an Error.
*/
function resolveVersion(explicit) {
	if (explicit !== void 0) {
		const parsed = VersionSchema.safeParse(explicit);
		if (parsed.success) return ok(parsed.data);
		return err(VERSION_ERROR);
	}
	if (typeof __KIDD_VERSION__ === "string") {
		const parsed = VersionSchema.safeParse(__KIDD_VERSION__);
		if (parsed.success) return ok(parsed.data);
	}
	return err(VERSION_ERROR);
}
/**
* Resolve the commands option to a {@link ResolvedCommands}.
*
* Accepts a directory string (triggers autoload), a static CommandMap,
* a Promise<CommandMap>, a structured {@link CommandsConfig},
* or undefined (loads `kidd.config.ts` and autoloads from its `commands` field,
* falling back to `'./commands'`).
*
* @private
* @param commands - The commands option from CliOptions.
* @returns Resolved commands with optional order, or undefined.
*/
async function resolveCommands(commands) {
	return match(commands).when(isString, async (dir) => ({ commands: await autoload({ dir }) })).with(P.instanceOf(Promise), async (p) => ({ commands: await p })).when(isCommandsConfig, (cfg) => resolveCommandsConfig(cfg)).when(isPlainObject, (cmds) => ({ commands: cmds })).otherwise(() => resolveCommandsFromConfig());
}
/**
* Resolve a structured {@link CommandsConfig} into a flat command map.
*
* When `path` is provided, autoloads from that directory. Otherwise uses the
* inline `commands` map (resolved if it is a promise).
*
* @private
* @param config - The structured commands configuration.
* @returns Resolved commands.
*/
async function resolveCommandsConfig(config) {
	const { path, commands: innerCommands } = config;
	return { commands: await match(innerCommands).when(() => isString(path), async () => autoload({ dir: path })).with(P.instanceOf(Promise), async (p) => p).when(isPlainObject, (cmds) => cmds).otherwise(() => ({})) };
}
/**
* Load `kidd.config.ts` and autoload commands from its `commands` field.
*
* Falls back to `'./commands'` when the config file is missing, fails to load,
* or does not specify a `commands` field.
*
* @private
* @returns A CommandMap autoloaded from the configured commands directory.
*/
async function resolveCommandsFromConfig() {
	const DEFAULT_COMMANDS_DIR = "./commands";
	const { loadConfig } = await import("@kidd-cli/config/utils");
	const [configError, configResult] = await loadConfig();
	if (configError || !configResult) return { commands: await autoload({ dir: DEFAULT_COMMANDS_DIR }) };
	return { commands: await autoload({ dir: configResult.config.commands ?? DEFAULT_COMMANDS_DIR }) };
}
/**
* Change the process working directory when `--cwd` is provided.
*
* Resolves the value to an absolute path and calls `process.chdir()` so
* that all downstream `process.cwd()` calls reflect the override.
*
* @private
* @param argv - The parsed argv record from yargs.
*/
function applyCwd(argv) {
	if (isString(argv.cwd)) process.chdir(resolve(argv.cwd));
}
/**
* Show help output when no command was matched.
*
* Prints the header (if configured) above the yargs help text. Skipped when
* `--help` was explicitly passed, since yargs already handles that case.
*
* @private
* @param params - The argv, commands, help options, and yargs program instance.
*/
function showNoCommandHelp({ argv, commands, help, program }) {
	if (!commands) return;
	if (argv.help) return;
	const header = extractHeader(help);
	if (header) {
		console.log(header);
		console.log();
	}
	program.showHelp("log");
}
/**
* Extract the header string from help options.
*
* @private
* @param help - The help options, possibly undefined.
* @returns The header string or undefined.
*/
function extractHeader(help) {
	if (!help) return;
	return help.header;
}
/**
* Extract the footer string from help options.
*
* @private
* @param help - The help options, possibly undefined.
* @returns The footer string or undefined.
*/
function extractFooter(help) {
	if (!help) return;
	return help.footer;
}
/**
* Resolve directory config into a {@link ResolvedDirs} with defaults.
*
* Both `local` and `global` default to `.<name>` when not provided.
*
* @private
* @param name - The CLI name used to derive defaults.
* @param dirs - Optional user-provided directory overrides.
* @returns Resolved dirs with both local and global guaranteed.
*/
function resolveDirs(name, dirs) {
	const defaultDir = `.${name}`;
	if (isNil(dirs)) return {
		global: defaultDir,
		local: defaultDir
	};
	return {
		global: match(dirs.global).with(P.nullish, () => defaultDir).when(isEmptyString, () => defaultDir).otherwise((v) => v),
		local: match(dirs.local).with(P.nullish, () => defaultDir).when(isEmptyString, () => defaultDir).otherwise((v) => v)
	};
}
/**
* Check whether a value is an empty string (after trimming whitespace).
*
* @private
* @param value - The value to check.
* @returns True when the value is an empty or whitespace-only string.
*/
function isEmptyString(value) {
	return value.trim().length === 0;
}
/**
* Apply display config settings that can only be set globally via clack's `updateSettings()`.
*
* Only `aliases` and `messages` require global application. All other
* display config values (including `guide`) are injected per-call by the
* context factories.
*
* @private
* @param display - The display config from CliOptions, if any.
*/
function applyDisplayGlobals(display) {
	if (display === void 0) return;
	const hasAliases = display.aliases !== void 0;
	const hasMessages = display.messages !== void 0;
	if (!hasAliases && !hasMessages) return;
	updateSettings({
		aliases: display.aliases,
		messages: display.messages
	});
}
//#endregion
export { autoload as i, cli_exports as n, command as r, cli as t };

//# sourceMappingURL=cli-9sfQca7V.js.map